/**
 * Motor_Casos client-side (variante demo) — parte de **correlación y
 * ciclo de vida de Casos** (tarea 2.1, R3.1, R3.2, R3.9).
 *
 * Este módulo implementa la creación, actualización y consolidación de
 * {@link Caso}s a partir de evidencia operacional correlacionada
 * (Anomalias, Alertas, presión, MNF, Cobertura_RF) que converge sobre
 * una misma **entidad raíz** dentro de una **ventana temporal de
 * correlación** configurable (default 24h):
 *
 *   - **Creación (R3.1):** si no existe un Caso abierto cuya entidad raíz
 *     sea la entidad de mayor jerarquía del Grafo_Entidades entre las
 *     entidades sobre las que converge la evidencia, se crea exactamente
 *     un Caso con esa raíz.
 *   - **Actualización (R3.2):** si existe un único Caso abierto con esa
 *     raíz y ventana solapada, se le agrega la evidencia conservando su
 *     entidad raíz.
 *   - **Consolidación (R3.9):** si dos o más Casos abiertos comparten la
 *     misma raíz y ventanas temporales solapadas, se consolidan en un
 *     único Caso preservando la trazabilidad a la evidencia original de
 *     cada Caso consolidado.
 *
 * Por cada delta de Caso (creado/actualizado/consolidado) se emite un
 * {@link EventoOperacional} en el Event_Store (append-only, R9).
 *
 * **Alcance.** La **correlación y ciclo de vida** (tarea 2.1) y el
 * **scoring** determinista de los nueve componentes (tarea 2.2,
 * {@link componentesDeCaso}, {@link scoreFromComponentes}, `score`) están
 * implementados. La **explainability**, el orden total, el timeline y las
 * acciones sugeridas se añaden en la tarea 2.7; esos métodos quedan como
 * **stubs marcados** que devuelven un `Result` degradado controlado. El
 * `score`/`explainability` de un Caso recién creado se inicializa como
 * **cálculo pendiente** (todos los componentes marcados como faltantes),
 * nunca como un valor inventado.
 *
 * La entidad raíz se determina con {@link rootOf} de `./graph` (R3.1),
 * reutilizando su jerarquía (la DMA agrupa al resto). No se duplican
 * entidades de la fundación: se referencian por `entidad_ref` (R17).
 *
 * Las funciones puras {@link entidadRaizDeEvidencia},
 * {@link aplicarEvidencia}, {@link consolidarCasosPorRaiz},
 * {@link derivarEvidencias} y los predicados de ventana/raíz se exportan
 * para que las tareas de testing (Property 13) las ejerciten sin
 * instanciar la clase ni tocar IndexedDB.
 */

import type {
  Caso,
  CasoDelta,
  ComponenteScore,
  ComponentesScore,
  EntidadRef,
  EstadoCaso,
  EventStore,
  Evidencia,
  EvidenciaCaso,
  EventoOperacional,
  ExplainabilityCaso,
  ContribucionExplain,
  CadenaCausal,
  VinculoCausal,
  Id,
  IsoTimestamp,
  MotorCasos,
  PesosScore,
  Result,
  ScoreCaso,
  AccionSugerida,
} from "../api/contracts";

import { nodeIdFor, rootOf, type GraphData } from "./graph";
import type { RealAlarma } from "../api/realData";

// ---------------------------------------------------------------------------
// Configuración
// ---------------------------------------------------------------------------

/** Ventana temporal de correlación por defecto: 24 horas (R3.1, R3.2). */
export const VENTANA_CORRELACION_DEFAULT_MS = 24 * 60 * 60 * 1000;

/** Los nueve componentes del Score_Caso (R3.3, R4.1). Orden canónico. */
export const COMPONENTES_SCORE: ComponenteScore[] = [
  "impacto_economico",
  "criticidad",
  "recurrencia",
  "presion",
  "mnf",
  "anomalias_iot",
  "cobertura_rf",
  "sla",
  "reincidencia",
];

/** Parámetros de correlación/ciclo de vida del Motor_Casos. */
export interface MotorCasosConfig {
  /** Ventana de correlación en milisegundos (default 24h). */
  ventanaCorrelacionMs: number;
}

/** Configuración por defecto del Motor_Casos demo. */
export const MOTOR_CASOS_CONFIG_DEFAULT: MotorCasosConfig = {
  ventanaCorrelacionMs: VENTANA_CORRELACION_DEFAULT_MS,
};

/** Estados en los que un Caso se considera **abierto** para correlación. */
const ESTADOS_ABIERTOS: ReadonlySet<EstadoCaso> = new Set<EstadoCaso>([
  "abierto",
  "en_progreso",
]);

// ---------------------------------------------------------------------------
// Helpers de tiempo y comparación
// ---------------------------------------------------------------------------

/** Instante actual en ISO-8601 UTC. */
function nowIso(): IsoTimestamp {
  return new Date().toISOString();
}

/** Genera un identificador único (UUID si está disponible). */
function generarId(prefijo: string): Id {
  try {
    const c = (globalThis as { crypto?: Crypto }).crypto;
    if (c && typeof c.randomUUID === "function") return `${prefijo}:${c.randomUUID()}`;
  } catch {
    /* fallback abajo */
  }
  return `${prefijo}:${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
}

/** Convierte un ISO-8601 a milisegundos epoch (NaN si no parseable). */
function ms(ts: IsoTimestamp): number {
  return Date.parse(ts);
}

/** Igualdad estructural de dos referencias de entidad. */
export function mismaEntidad(a: EntidadRef, b: EntidadRef): boolean {
  return a.tipo === b.tipo && a.id === b.id;
}

/** ¿El Caso está abierto (sujeto a correlación)? */
export function esCasoAbierto(caso: Caso): boolean {
  return ESTADOS_ABIERTOS.has(caso.estado);
}

// ---------------------------------------------------------------------------
// Ventana temporal de un Caso
// ---------------------------------------------------------------------------

/** Ventana temporal `[inicio, fin]` (en ms) de un Caso según su evidencia. */
export interface VentanaCaso {
  inicio: number;
  fin: number;
}

/**
 * Calcula la ventana temporal de un Caso como el rango `[min, max]` de
 * los timestamps de su evidencia. Si el Caso no tiene evidencia con
 * timestamps válidos, cae a su `fecha_creacion`.
 */
export function ventanaDeCaso(caso: Caso): VentanaCaso {
  const tiempos = caso.evidencia
    .map((e) => ms(e.ts))
    .filter((t) => Number.isFinite(t));
  if (tiempos.length === 0) {
    const base = ms(caso.fecha_creacion);
    const seguro = Number.isFinite(base) ? base : 0;
    return { inicio: seguro, fin: seguro };
  }
  return { inicio: Math.min(...tiempos), fin: Math.max(...tiempos) };
}

/**
 * ¿La evidencia con timestamp `ts` converge dentro de la ventana de
 * correlación del Caso? Es cierto cuando `ts` cae en el intervalo de la
 * ventana del Caso extendido en `ventanaMs` a ambos lados, es decir, la
 * nueva evidencia está a lo sumo a `ventanaMs` de la evidencia existente
 * (R3.1, R3.2).
 */
export function evidenciaEnVentana(caso: Caso, ts: IsoTimestamp, ventanaMs: number): boolean {
  const t = ms(ts);
  if (!Number.isFinite(t)) return false;
  const v = ventanaDeCaso(caso);
  return t >= v.inicio - ventanaMs && t <= v.fin + ventanaMs;
}

/**
 * ¿Las ventanas de dos Casos se solapan dentro de la tolerancia
 * `ventanaMs`? Usado para consolidar Casos con la misma raíz (R3.9).
 */
export function ventanasSolapadas(a: Caso, b: Caso, ventanaMs: number): boolean {
  const va = ventanaDeCaso(a);
  const vb = ventanaDeCaso(b);
  return va.inicio - ventanaMs <= vb.fin && vb.inicio - ventanaMs <= va.fin;
}

// ---------------------------------------------------------------------------
// Entidad raíz de la evidencia (R3.1) — vía rootOf de graph.ts
// ---------------------------------------------------------------------------

/**
 * Determina la **entidad raíz** sobre la que converge una evidencia: la
 * entidad de mayor jerarquía del Grafo_Entidades entre las entidades de
 * la evidencia (R3.1). Reutiliza {@link rootOf} de `./graph` para la
 * jerarquía y devuelve la {@link EntidadRef} correspondiente.
 */
export function entidadRaizDeEvidencia(evidencia: Evidencia): Result<EntidadRef> {
  const entidades = evidencia.entidades ?? [];
  if (entidades.length === 0) {
    return { ok: false, error: "La evidencia no referencia ninguna entidad; no hay entidad raíz." };
  }
  const raiz = rootOf(entidades);
  if (!raiz.ok) return raiz;
  // Mapear el node_id elegido de vuelta a su EntidadRef original.
  const ref = entidades.find((e) => nodeIdFor(e) === raiz.value);
  if (!ref) {
    return { ok: false, error: `No se pudo resolver la EntidadRef de la raíz '${raiz.value}'.` };
  }
  return { ok: true, value: ref };
}

// ---------------------------------------------------------------------------
// Construcción de Casos y evidencia
// ---------------------------------------------------------------------------

/**
 * Score inicial de un Caso recién creado: **cálculo pendiente**. El
 * scoring real (combinación lineal de los nueve componentes) lo calcula
 * la tarea 2.2; hasta entonces, el Caso se marca como cálculo parcial
 * con todos los componentes faltantes, sin inventar un valor (R3.8).
 */
export function scorePendiente(): ScoreCaso {
  return { valor: 0, parcial: true, componentes_faltantes: [...COMPONENTES_SCORE] };
}

/**
 * Explainability inicial vacía. La descomposición con signo de los nueve
 * componentes la construye la tarea 2.7 (R4.1–R4.3).
 */
export function explainabilityPendiente(): ExplainabilityCaso {
  return { contribuciones: [] };
}

/** Proyecta una {@link Evidencia} entrante a una {@link EvidenciaCaso}. */
function aEvidenciaCaso(evidencia: Evidencia): EvidenciaCaso {
  return { evidencia_ref: evidencia.evidencia_ref, ts: evidencia.ts };
}

/** Une evidencia evitando duplicados por `evidencia_ref`. */
function unirEvidencia(existente: EvidenciaCaso[], nuevas: EvidenciaCaso[]): EvidenciaCaso[] {
  const vistos = new Set(existente.map((e) => nodeIdFor(e.evidencia_ref)));
  const resultado = [...existente];
  for (const ev of nuevas) {
    const clave = nodeIdFor(ev.evidencia_ref);
    if (!vistos.has(clave)) {
      vistos.add(clave);
      resultado.push(ev);
    }
  }
  return resultado;
}

/** Crea un Caso nuevo abierto a partir de la primera evidencia (R3.1). */
function crearCaso(raiz: EntidadRef, evidencia: Evidencia, caso_id: Id): Caso {
  return {
    caso_id,
    entidad_raiz_ref: raiz,
    estado: "abierto",
    fecha_creacion: evidencia.ts,
    score: scorePendiente(),
    evidencia: [aEvidenciaCaso(evidencia)],
    explainability: explainabilityPendiente(),
    acciones_sugeridas: [],
    ordenes_ref: [],
  };
}

/** Desempate determinista para elegir el Caso destino de una consolidación. */
function ordenConsolidacion(a: Caso, b: Caso): number {
  const fa = ms(a.fecha_creacion);
  const fb = ms(b.fecha_creacion);
  if (Number.isFinite(fa) && Number.isFinite(fb) && fa !== fb) return fa - fb;
  if (a.fecha_creacion !== b.fecha_creacion) return a.fecha_creacion < b.fecha_creacion ? -1 : 1;
  return a.caso_id < b.caso_id ? -1 : a.caso_id > b.caso_id ? 1 : 0;
}

// ---------------------------------------------------------------------------
// Núcleo puro: aplicar evidencia (crear / actualizar / consolidar)
// ---------------------------------------------------------------------------

/** Opciones del paso puro {@link aplicarEvidencia}. */
export interface AplicarEvidenciaOpts {
  /** Ventana de correlación en ms. */
  ventanaMs: number;
  /** Generador del `caso_id` para Casos nuevos (inyectable en tests). */
  nuevoCasoId: () => Id;
}

/** Resultado del paso puro {@link aplicarEvidencia}. */
export interface AplicarEvidenciaResultado {
  /** Lista de Casos resultante tras aplicar la evidencia. */
  casos: Caso[];
  /** Delta producido (creación/actualización/consolidación). */
  delta: CasoDelta;
}

/**
 * **Función pura** que aplica una pieza de evidencia a un conjunto de
 * Casos, implementando el ciclo de vida (R3.1, R3.2, R3.9):
 *
 *   1. Resuelve la entidad raíz de la evidencia (mayor jerarquía).
 *   2. Identifica los Casos **abiertos** con esa misma raíz cuya ventana
 *      se solapa con el timestamp de la evidencia.
 *   3. Si no hay ninguno → **crea** un Caso nuevo.
 *   4. Si hay exactamente uno → **agrega** la evidencia a ese Caso.
 *   5. Si hay dos o más → **consolida** todos en uno (el de menor
 *      `fecha_creacion`, desempatando por `caso_id`), uniendo la
 *      evidencia y preservando la trazabilidad (`consolidados`).
 *
 * No muta sus argumentos: devuelve una nueva lista de Casos.
 */
export function aplicarEvidencia(
  casos: Caso[],
  evidencia: Evidencia,
  opts: AplicarEvidenciaOpts,
): Result<AplicarEvidenciaResultado> {
  const raiz = entidadRaizDeEvidencia(evidencia);
  if (!raiz.ok) return raiz;
  const raizRef = raiz.value;

  const coincidentes = casos.filter(
    (c) =>
      esCasoAbierto(c) &&
      mismaEntidad(c.entidad_raiz_ref, raizRef) &&
      evidenciaEnVentana(c, evidencia.ts, opts.ventanaMs),
  );

  // 3) Sin Caso abierto con esa raíz en ventana → crear (R3.1).
  if (coincidentes.length === 0) {
    const nuevo = crearCaso(raizRef, evidencia, opts.nuevoCasoId());
    return {
      ok: true,
      value: { casos: [...casos, nuevo], delta: { caso: nuevo, operacion: "creado" } },
    };
  }

  // Elegir Caso destino determinista (menor fecha_creacion, luego caso_id).
  const ordenados = [...coincidentes].sort(ordenConsolidacion);
  const destino = ordenados[0];
  const absorbidos = ordenados.slice(1);

  // Unir la evidencia existente del destino + absorbidos + la nueva.
  let evidenciaUnida = destino.evidencia;
  for (const c of absorbidos) {
    evidenciaUnida = unirEvidencia(evidenciaUnida, c.evidencia);
  }
  evidenciaUnida = unirEvidencia(evidenciaUnida, [aEvidenciaCaso(evidencia)]);

  const casoActualizado: Caso = {
    ...destino,
    // Conserva la entidad raíz (R3.2) y la fecha de creación más antigua.
    entidad_raiz_ref: destino.entidad_raiz_ref,
    evidencia: evidenciaUnida,
    estado: "abierto",
  };

  const idsAbsorbidos = new Set(absorbidos.map((c) => c.caso_id));
  const resto = casos.filter(
    (c) => c.caso_id !== destino.caso_id && !idsAbsorbidos.has(c.caso_id),
  );

  // 5) Consolidación (R3.9) o 4) actualización (R3.2).
  const delta: CasoDelta =
    absorbidos.length > 0
      ? { caso: casoActualizado, operacion: "consolidado", consolidados: absorbidos.map((c) => c.caso_id) }
      : { caso: casoActualizado, operacion: "actualizado" };

  return { ok: true, value: { casos: [...resto, casoActualizado], delta } };
}

/**
 * **Función pura** que consolida, sin nueva evidencia, todos los Casos
 * abiertos que comparten la misma entidad raíz y ventanas temporales
 * solapadas en un único Caso por raíz, preservando la trazabilidad a la
 * evidencia original (R3.9). Devuelve la lista consolidada y los deltas
 * de consolidación producidos.
 */
export function consolidarCasosPorRaiz(
  casos: Caso[],
  ventanaMs: number,
): { casos: Caso[]; deltas: CasoDelta[] } {
  const abiertos = casos.filter(esCasoAbierto);
  const otros = casos.filter((c) => !esCasoAbierto(c));

  // Agrupar Casos abiertos por entidad raíz.
  const porRaiz = new Map<string, Caso[]>();
  for (const c of abiertos) {
    const clave = nodeIdFor(c.entidad_raiz_ref);
    const grupo = porRaiz.get(clave);
    if (grupo) grupo.push(c);
    else porRaiz.set(clave, [c]);
  }

  const consolidados: Caso[] = [];
  const deltas: CasoDelta[] = [];

  for (const grupo of porRaiz.values()) {
    // Particionar el grupo en componentes de ventanas solapadas (unión por
    // solapamiento transitivo). Algoritmo simple O(n²) suficiente para demo.
    const pendientes = [...grupo].sort(ordenConsolidacion);
    while (pendientes.length > 0) {
      const componente: Caso[] = [pendientes.shift()!];
      let creció = true;
      while (creció) {
        creció = false;
        for (let i = pendientes.length - 1; i >= 0; i--) {
          if (componente.some((c) => ventanasSolapadas(c, pendientes[i], ventanaMs))) {
            componente.push(pendientes.splice(i, 1)[0]);
            creció = true;
          }
        }
      }

      componente.sort(ordenConsolidacion);
      const destino = componente[0];
      const absorbidos = componente.slice(1);

      if (absorbidos.length === 0) {
        consolidados.push(destino);
        continue;
      }

      let evidenciaUnida = destino.evidencia;
      for (const c of absorbidos) evidenciaUnida = unirEvidencia(evidenciaUnida, c.evidencia);
      const fusionado: Caso = { ...destino, evidencia: evidenciaUnida, estado: "abierto" };
      consolidados.push(fusionado);
      deltas.push({
        caso: fusionado,
        operacion: "consolidado",
        consolidados: absorbidos.map((c) => c.caso_id),
      });
    }
  }

  return { casos: [...otros, ...consolidados], deltas };
}

// ---------------------------------------------------------------------------
// Derivación de evidencia desde alarmas.json + Grafo (R3.1)
// ---------------------------------------------------------------------------

/** Mapa de flags de alarma de `alarmas.json` a su etiqueta de Anomalia. */
const TIPOS_ALARMA: Array<{ campo: keyof RealAlarma; etiqueta: string }> = [
  { campo: "alarm_leakage", etiqueta: "fuga" },
  { campo: "alarm_burst", etiqueta: "reventon" },
  { campo: "alarm_backflow", etiqueta: "backflow" },
  { campo: "alarm_dry", etiqueta: "tuberia_seca" },
  { campo: "alarm_manipulation", etiqueta: "manipulacion" },
  { campo: "alarm_battery", etiqueta: "bateria" },
  { campo: "alarm_permanent", etiqueta: "permanente" },
];

/**
 * Deriva piezas de {@link Evidencia} a partir de `alarmas.json` y la
 * jerarquía del Grafo (DMA agrupa Medidor y Anomalia). Cada flag de
 * alarma activo de un medidor con DMA válida produce una evidencia cuyas
 * entidades son `[Anomalia, Medidor, DMA]`, de modo que la entidad raíz
 * resuelta sea la DMA (mayor jerarquía, R3.1). Las alarmas sin DMA válida
 * se omiten (dato incompleto), igual que en la proyección del Grafo.
 *
 * Función **pura**: mismos datos → misma lista de evidencia, en orden.
 */
export function derivarEvidencias(data: GraphData): Evidencia[] {
  const dmas = data.dmas ?? [];
  const alarmas = data.alarmas ?? [];
  const evidencias: Evidencia[] = [];

  for (const al of alarmas) {
    if (!Number.isInteger(al.dma_idx) || al.dma_idx < 0 || al.dma_idx >= dmas.length) {
      continue; // sin DMA válida → dato incompleto, se omite
    }
    const dma = dmas[al.dma_idx];
    const dmaRef: EntidadRef = { tipo: "DMA", id: dma.codigo };
    const medRef: EntidadRef = { tipo: "Medidor", id: al.device };

    for (const { campo, etiqueta } of TIPOS_ALARMA) {
      if (al[campo] !== true) continue;
      const anomRef: EntidadRef = { tipo: "Anomalia", id: `${al.device}:${etiqueta}` };
      evidencias.push({
        evidencia_ref: anomRef,
        entidades: [anomRef, medRef, dmaRef],
        ts: al.root_timestamp,
      });
    }
  }

  return evidencias;
}

// ---------------------------------------------------------------------------
// Scoring de los nueve componentes (tarea 2.2, R3.3, R3.5, R3.6, R3.7, R3.8)
// ---------------------------------------------------------------------------

/**
 * Acota un valor al rango `[0, 100]` del Score_Caso (R3.3). Los valores
 * no finitos (`NaN`, `±Infinity`) caen a `0` para no propagar estados
 * inválidos al puntaje.
 */
export function clamp_0_100(x: number): number {
  if (!Number.isFinite(x)) return 0;
  if (x < 0) return 0;
  if (x > 100) return 100;
  return x;
}

/**
 * Vector de pesos por defecto: reparto **uniforme** sobre los nueve
 * componentes (cada `peso_k = 1/9`, no negativos y con suma 1, R3.3).
 * Útil como `pesos` válido por defecto para vistas/tests; las firmas del
 * contrato siguen recibiendo el vector de pesos explícitamente.
 */
export const PESOS_SCORE_DEFAULT: PesosScore = COMPONENTES_SCORE.reduce(
  (acc, k) => {
    acc[k] = 1 / COMPONENTES_SCORE.length;
    return acc;
  },
  {} as PesosScore,
);

/**
 * Perfil determinista de componentes que aporta cada tipo de Anomalia
 * (etiqueta derivada de `alarmas.json`). Cada valor está normalizado al
 * rango `[0, 100]`. Solo se materializan los componentes para los que el
 * tipo de evidencia aporta señal; el resto queda **ausente** (cálculo
 * parcial, R3.8). Tabla pura → derivación determinista (R3.5).
 */
const PERFIL_ANOMALIA: Readonly<Record<string, ComponentesScore>> = {
  fuga: { impacto_economico: 70, criticidad: 55, mnf: 65, anomalias_iot: 60 },
  reventon: { impacto_economico: 95, criticidad: 90, presion: 80, anomalias_iot: 85 },
  backflow: { criticidad: 75, anomalias_iot: 70, presion: 50 },
  tuberia_seca: { criticidad: 60, presion: 70, anomalias_iot: 55 },
  manipulacion: { impacto_economico: 60, criticidad: 65, anomalias_iot: 50 },
  bateria: { cobertura_rf: 40, anomalias_iot: 20 },
  permanente: { recurrencia: 60, sla: 55, reincidencia: 50, anomalias_iot: 45 },
};

/** Extrae la etiqueta de Anomalia de un `evidencia_ref.id` `device:etiqueta`. */
function etiquetaDeRef(id: Id): string {
  const idx = id.lastIndexOf(":");
  return idx >= 0 ? id.slice(idx + 1) : id;
}

/**
 * **Función pura** que deriva los nueve componentes del Score_Caso desde
 * la evidencia y atributos de un {@link Caso}, de forma **determinista**
 * (mismos datos → mismos componentes, R3.5). Cada componente queda en
 * `[0, 100]`; los componentes sin señal en la evidencia se **omiten** del
 * resultado, de modo que el scoring los trate como cálculo parcial (R3.8).
 *
 * Agregación por **máximo** sobre la evidencia: a mayor cantidad/intensidad
 * de evidencia, los componentes no decrecen, lo que es consistente con la
 * monotonicidad del puntaje (R3.6). Además:
 *   - `recurrencia` crece con la cantidad de piezas de evidencia.
 *   - `reincidencia` crece con etiquetas de Anomalia repetidas en el Caso.
 *
 * Exportada para que las Properties 9/10/11 (tareas 2.3–2.5) la ejerciten
 * sin instanciar la clase ni tocar almacenamiento.
 */
export function componentesDeCaso(caso: Caso): ComponentesScore {
  const acc: ComponentesScore = {};
  const setMax = (k: ComponenteScore, v: number): void => {
    const val = clamp_0_100(v);
    const prev = acc[k];
    acc[k] = prev === undefined ? val : Math.max(prev, val);
  };

  const etiquetas: string[] = [];
  for (const ev of caso.evidencia) {
    if (ev.evidencia_ref.tipo !== "Anomalia") continue;
    const etq = etiquetaDeRef(ev.evidencia_ref.id);
    etiquetas.push(etq);
    const perfil = PERFIL_ANOMALIA[etq];
    if (!perfil) continue;
    for (const comp of COMPONENTES_SCORE) {
      const v = perfil[comp];
      if (v !== undefined) setMax(comp, v);
    }
  }

  // recurrencia: crece con la cantidad de evidencia (determinista, monótona).
  const n = caso.evidencia.length;
  if (n > 0) setMax("recurrencia", Math.min(100, n * 20));

  // reincidencia: etiquetas de Anomalia repetidas dentro del mismo Caso.
  const repetidos = etiquetas.length - new Set(etiquetas).size;
  if (repetidos > 0) setMax("reincidencia", Math.min(100, repetidos * 25));

  return acc;
}

/**
 * **Función pura** que calcula el Score_Caso a partir de un vector de
 * componentes y un vector de pesos (R3.3, R3.5, R3.6, R3.8):
 *
 *   `score = clamp_0_100( Σ_k peso_k · comp_k )`
 *
 * sobre los componentes **disponibles** (`comp_k ∈ [0,100]`, `peso_k ≥ 0`).
 * Un componente sin dato no contribuye a la suma (peso efectivo cero),
 * se enumera en `componentes_faltantes` en el orden canónico de
 * {@link COMPONENTES_SCORE} y marca el resultado como **parcial** (R3.8).
 *
 * Propiedades garantizadas:
 *   - **Determinismo (R3.5):** función pura de `(componentes, pesos)`.
 *   - **Rango/linealidad (R3.3):** con los nueve componentes presentes,
 *     pesos que suman 1 y `comp_k ∈ [0,100]`, el resultado es la
 *     combinación lineal acotada a `[0,100]`.
 *   - **Monotonicidad (R3.6):** al aumentar un componente presente con
 *     los demás y los pesos constantes, la suma no decrece y el `clamp`
 *     es monótono no decreciente, por lo que el score no baja.
 *
 * Exportada para las Properties 9/10/11 (tareas 2.3–2.5).
 */
export function scoreFromComponentes(
  componentes: ComponentesScore,
  pesos: PesosScore,
): ScoreCaso {
  let suma = 0;
  const faltantes: ComponenteScore[] = [];

  for (const k of COMPONENTES_SCORE) {
    const comp = componentes[k];
    if (comp === undefined || !Number.isFinite(comp)) {
      faltantes.push(k);
      continue;
    }
    const peso = Math.max(0, pesos[k] ?? 0);
    suma += peso * clamp_0_100(comp);
  }

  return {
    valor: clamp_0_100(suma),
    parcial: faltantes.length > 0,
    componentes_faltantes: faltantes,
  };
}

// ---------------------------------------------------------------------------
// Orden total determinista de Casos (tarea 2.7, R3.4 — Property 12)
// ---------------------------------------------------------------------------

/**
 * **Función pura** que produce el **orden total determinista** de un
 * conjunto de Casos (R3.4):
 *
 *   1. **Descendente** por `score.valor` (mayor prioridad primero).
 *   2. Empate de score → **ascendente** por `fecha_creacion`.
 *   3. Empate de fecha → **ascendente** por `caso_id`.
 *
 * El orden es **total** y **determinista**: para cualquier par de Casos
 * el comparador devuelve un signo estable, de modo que dos invocaciones
 * sobre la misma entrada producen exactamente la misma secuencia
 * (incluso ante empates de score y de fecha). No muta la entrada.
 *
 * Exportada para que la Property 12 la ejercite sin instanciar la clase.
 */
export function ordenarCasos(casos: Caso[]): Caso[] {
  return [...casos].sort(compararCasos);
}

/** Comparador del orden total determinista de Casos (R3.4, Property 12). */
export function compararCasos(a: Caso, b: Caso): number {
  // 1) Desc por score.valor. Valores no finitos caen al final (tratados como -∞).
  const sa = Number.isFinite(a.score?.valor) ? a.score.valor : Number.NEGATIVE_INFINITY;
  const sb = Number.isFinite(b.score?.valor) ? b.score.valor : Number.NEGATIVE_INFINITY;
  if (sa !== sb) return sb - sa;

  // 2) Empate de score → asc por fecha_creacion (numérica si parseable, luego lexicográfica).
  const fa = ms(a.fecha_creacion);
  const fb = ms(b.fecha_creacion);
  if (Number.isFinite(fa) && Number.isFinite(fb) && fa !== fb) return fa - fb;
  if (a.fecha_creacion !== b.fecha_creacion) return a.fecha_creacion < b.fecha_creacion ? -1 : 1;

  // 3) Empate de fecha → asc por caso_id.
  return a.caso_id < b.caso_id ? -1 : a.caso_id > b.caso_id ? 1 : 0;
}

// ---------------------------------------------------------------------------
// Explainability_Caso (tarea 2.7, R4.1–R4.3 — Property 14)
// ---------------------------------------------------------------------------

/** Etiquetas legibles por componente para la Explainability_Caso (R4.1). */
const ETIQUETA_COMPONENTE: Readonly<Record<ComponenteScore, string>> = {
  impacto_economico: "Impacto económico",
  criticidad: "Criticidad",
  recurrencia: "Recurrencia",
  presion: "Presión",
  mnf: "MNF (caudal mínimo nocturno)",
  anomalias_iot: "Anomalías IoT",
  cobertura_rf: "Cobertura RF",
  sla: "SLA",
  reincidencia: "Reincidencia",
};

/**
 * **Función pura** que construye la {@link ExplainabilityCaso} a partir
 * de un vector de componentes y de pesos, de forma consistente con
 * {@link scoreFromComponentes} (R4.1, R4.2, R4.3 — Property 14):
 *
 *   - Enumera **los nueve** componentes en el **orden canónico** de
 *     {@link COMPONENTES_SCORE}; ninguno se omite (R4.3).
 *   - Para cada componente disponible, `contribucion_k = peso_k · comp_k`
 *     (en puntos), acotada al rango `[-100, 100]`. Como `peso_k ≥ 0` y
 *     `comp_k ∈ [0, 100]`, la contribución cae en `[0, 100]`; el `clamp`
 *     defiende el rango ante pesos/insumos atípicos (R4.1).
 *   - Un componente **sin datos** (ausente o no finito) aparece con
 *     contribución `0` y `no_disponible = true`, nunca omitido (R4.3).
 *   - La **suma de las contribuciones reconstruye el Score_Caso ±1**
 *     (R4.2): ambas usan los mismos `peso_k · comp_k` sobre los mismos
 *     componentes disponibles, salvo el redondeo del `clamp` final del
 *     score, acotado a un punto.
 *
 * Exportada como `explainFromComponentes` para que la Property 14 la
 * ejercite sin instanciar la clase ni tocar almacenamiento.
 */
export function explainFromComponentes(
  componentes: ComponentesScore,
  pesos: PesosScore,
): ExplainabilityCaso {
  const contribuciones: ContribucionExplain[] = COMPONENTES_SCORE.map((k) => {
    const comp = componentes[k];
    const disponible = comp !== undefined && Number.isFinite(comp);
    if (!disponible) {
      // Componente sin datos: contribución 0 y marca de no disponible (R4.3).
      return { componente: k, contribucion: 0, etiqueta: ETIQUETA_COMPONENTE[k], no_disponible: true };
    }
    const peso = Math.max(0, pesos[k] ?? 0);
    const contribucion = clamp_neg100_100(peso * clamp_0_100(comp as number));
    return { componente: k, contribucion, etiqueta: ETIQUETA_COMPONENTE[k], no_disponible: false };
  });
  return { contribuciones };
}

/** Acota un valor al rango `[-100, 100]` de una contribución (R4.1). */
export function clamp_neg100_100(x: number): number {
  if (!Number.isFinite(x)) return 0;
  if (x < -100) return -100;
  if (x > 100) return 100;
  return x;
}

// ---------------------------------------------------------------------------
// Cadena_Causal del Caso (tarea 2.7, R4.4)
// ---------------------------------------------------------------------------

/**
 * **Función pura** que construye la {@link CadenaCausal} de un Caso
 * (R4.4): por cada pieza de evidencia se crea un vínculo dirigido
 * `evidencia → entidad_raiz`, indicando que la evidencia contribuye a
 * explicar el Caso enraizado en esa entidad. Los vínculos se ordenan por
 * el `ts` de la evidencia (no decreciente) y, ante empate, por el id de
 * la referencia, para que la cadena sea **determinista**. Caso sin
 * evidencia → cadena vacía (sin vínculos inventados).
 *
 * Exportada para reutilización en vistas y tests.
 */
export function cadenaCausalDeCaso(caso: Caso): CadenaCausal {
  const ordenada = [...caso.evidencia].sort((a, b) => {
    const ta = ms(a.ts);
    const tb = ms(b.ts);
    if (Number.isFinite(ta) && Number.isFinite(tb) && ta !== tb) return ta - tb;
    if (a.ts !== b.ts) return a.ts < b.ts ? -1 : 1;
    const ia = nodeIdFor(a.evidencia_ref);
    const ib = nodeIdFor(b.evidencia_ref);
    return ia < ib ? -1 : ia > ib ? 1 : 0;
  });
  const vinculos: VinculoCausal[] = ordenada.map((ev) => ({
    desde: ev.evidencia_ref,
    hacia: caso.entidad_raiz_ref,
    relacion: `${ev.evidencia_ref.tipo} contribuye al Caso de ${caso.entidad_raiz_ref.tipo} ${caso.entidad_raiz_ref.id}`,
  }));
  return { vinculos };
}

// ---------------------------------------------------------------------------
// Acciones sugeridas vinculadas al Registro (tarea 2.7, R4.7–R4.9 — Property 16)
// ---------------------------------------------------------------------------

/** Máximo de acciones sugeridas por Caso (R4.7). */
export const MAX_ACCIONES_SUGERIDAS = 10;

/**
 * Catálogo de `accion_semantica_id` por convención (referencias al
 * Registro_Acciones_Semanticas que materializa la tarea 6.1). Aquí solo
 * se referencian por id estable; el Registro valida su declaración.
 */
export const ACCION_ABRIR_ORDEN_INSPECCION = "abrir_orden_inspeccion";
export const ACCION_PROGRAMAR_INSPECCION = "programar_inspeccion";
export const ACCION_CERRAR_VALVULA = "cerrar_valvula";
export const ACCION_NOTIFICAR_CUADRILLA = "notificar_cuadrilla";
export const ACCION_REVISAR_RF = "revisar_cobertura_rf";

/**
 * Reglas deterministas etiqueta-de-Anomalia → acción sugerida. El
 * mapeo es puro (mismos datos → mismas acciones) y cada acción referencia
 * un `accion_semantica_id` del catálogo por convención (R4.7, R4.9).
 */
const REGLAS_ACCION: Readonly<Record<string, { accion: Id; razonamiento: string }>> = {
  reventon: {
    accion: ACCION_CERRAR_VALVULA,
    razonamiento:
      "Reventón detectado: cerrar la válvula aguas arriba acota el impacto downstream antes de la reparación.",
  },
  fuga: {
    accion: ACCION_ABRIR_ORDEN_INSPECCION,
    razonamiento:
      "Fuga con impacto económico y MNF elevados: abrir Orden_Inspeccion para localización y reparación en terreno.",
  },
  backflow: {
    accion: ACCION_ABRIR_ORDEN_INSPECCION,
    razonamiento:
      "Backflow detectado: abrir Orden_Inspeccion para verificar válvula antirretorno e integridad de la red.",
  },
  tuberia_seca: {
    accion: ACCION_PROGRAMAR_INSPECCION,
    razonamiento:
      "Tubería seca / presión anómala: programar inspección para confirmar continuidad de servicio.",
  },
  manipulacion: {
    accion: ACCION_NOTIFICAR_CUADRILLA,
    razonamiento:
      "Indicio de manipulación: notificar a la Cuadrilla para verificación presencial del medidor.",
  },
  bateria: {
    accion: ACCION_REVISAR_RF,
    razonamiento:
      "Batería baja / cobertura degradada: revisar el enlace RF del dispositivo para sostener la telemetría.",
  },
  permanente: {
    accion: ACCION_PROGRAMAR_INSPECCION,
    razonamiento:
      "Alarma permanente / reincidencia: programar inspección para atender la recurrencia del problema.",
  },
};

/**
 * **Función pura** que propone entre **0 y 10** acciones sugeridas para
 * un Caso (R4.7, R4.8, R4.9 — Property 16):
 *
 *   - Recorre la evidencia de tipo Anomalia del Caso y, por cada
 *     **etiqueta** con regla aplicable, genera una acción que referencia
 *     un `accion_semantica_id` del catálogo por convención (R4.7).
 *   - Cada acción adjunta `razonamiento` **no vacío** y `evidencia` con
 *     las referencias que la respaldan (R4.9).
 *   - Se **deduplica por `accion_semantica_id`** (una acción por tipo),
 *     agregando la evidencia de todas las piezas que la motivan, y se
 *     **acota a {@link MAX_ACCIONES_SUGERIDAS}** (R4.7).
 *   - **Sin acción aplicable → conjunto vacío** (R4.8); nunca se propone
 *     una acción no vinculada al catálogo.
 *
 * Determinista: el orden de salida sigue el orden canónico de
 * {@link COMPONENTES_SCORE}... no aplica; aquí se ordena por la primera
 * aparición de cada acción recorriendo la evidencia ya ordenada por id,
 * de modo que la salida sea estable.
 *
 * Exportada para reutilización en vistas y tests.
 */
export function suggestActionsForCaso(caso: Caso): AccionSugerida[] {
  // Orden estable de la evidencia para determinismo (por id de referencia).
  const evidenciaOrdenada = [...caso.evidencia].sort((a, b) => {
    const ia = nodeIdFor(a.evidencia_ref);
    const ib = nodeIdFor(b.evidencia_ref);
    return ia < ib ? -1 : ia > ib ? 1 : 0;
  });

  // Acumula por accion_semantica_id, uniendo evidencia de soporte sin duplicar.
  const porAccion = new Map<Id, { razonamiento: string; evidencia: EntidadRef[]; vistos: Set<string> }>();
  const orden: Id[] = [];

  for (const ev of evidenciaOrdenada) {
    if (ev.evidencia_ref.tipo !== "Anomalia") continue;
    const etq = etiquetaDeRef(ev.evidencia_ref.id);
    const regla = REGLAS_ACCION[etq];
    if (!regla) continue;

    let entrada = porAccion.get(regla.accion);
    if (!entrada) {
      entrada = { razonamiento: regla.razonamiento, evidencia: [], vistos: new Set<string>() };
      porAccion.set(regla.accion, entrada);
      orden.push(regla.accion);
    }
    const clave = nodeIdFor(ev.evidencia_ref);
    if (!entrada.vistos.has(clave)) {
      entrada.vistos.add(clave);
      entrada.evidencia.push(ev.evidencia_ref);
    }
  }

  const acciones: AccionSugerida[] = orden.map((accion) => {
    const entrada = porAccion.get(accion)!;
    return {
      accion_semantica_id: accion,
      razonamiento: entrada.razonamiento,
      evidencia: entrada.evidencia,
    };
  });

  // Acota a [0, 10] (R4.7). Sin reglas aplicables → vacío (R4.8).
  return acciones.slice(0, MAX_ACCIONES_SUGERIDAS);
}

// ---------------------------------------------------------------------------
// Timeline del Caso (tarea 2.7, R4.5 — Property 15)
// ---------------------------------------------------------------------------

/**
 * **Función pura** que ordena una lista de {@link EventoOperacional} por
 * `ts` en orden temporal **no decreciente** (R4.5, Property 15). Ante
 * empate de `ts` desempata por `seq` y luego por `event_id` para que el
 * orden sea total y determinista. No muta la entrada.
 */
export function ordenarTimeline(eventos: EventoOperacional[]): EventoOperacional[] {
  return [...eventos].sort((a, b) => {
    const ta = ms(a.ts);
    const tb = ms(b.ts);
    if (Number.isFinite(ta) && Number.isFinite(tb) && ta !== tb) return ta - tb;
    if (a.ts !== b.ts) return a.ts < b.ts ? -1 : 1;
    const sa = Number.isFinite(a.seq) ? a.seq : 0;
    const sb = Number.isFinite(b.seq) ? b.seq : 0;
    if (sa !== sb) return sa - sb;
    return a.event_id < b.event_id ? -1 : a.event_id > b.event_id ? 1 : 0;
  });
}

// ---------------------------------------------------------------------------
// MotorCasosLocal — implementación demo de la interfaz MotorCasos
// ---------------------------------------------------------------------------

/** Opciones de construcción del {@link MotorCasosLocal}. */
export interface MotorCasosLocalOptions {
  /** Event_Store donde emitir los Eventos_Operacionales de cada delta (R9). */
  eventStore?: EventStore;
  /** Parámetros de correlación/ciclo de vida. */
  config?: MotorCasosConfig;
  /** Casos iniciales (para rehidratar estado o tests). */
  casosIniciales?: Caso[];
  /**
   * Hook a Observabilidad: invocado con los componentes no calculados
   * cuando un Score_Caso resulta parcial (R3.8). Inyectable; si se omite,
   * el cálculo parcial se reporta igualmente en `ScoreCaso.componentes_faltantes`.
   */
  onComponentesFaltantes?: (caso_id: Id, faltantes: ComponenteScore[]) => void;
}

/**
 * Motor_Casos client-side que implementa {@link MotorCasos} para la
 * parte de **correlación y ciclo de vida** (tarea 2.1). Mantiene en
 * memoria la lista de Casos abiertos y emite un {@link EventoOperacional}
 * por cada delta. Los métodos de scoring/explainability/orden/timeline/
 * acciones quedan como stubs hasta las tareas 2.2 y 2.7.
 */
export class MotorCasosLocal implements MotorCasos {
  private readonly config: MotorCasosConfig;
  private readonly eventStore?: EventStore;
  private readonly onComponentesFaltantes?: (caso_id: Id, faltantes: ComponenteScore[]) => void;
  private casosState: Caso[];
  private contador = 0;

  constructor(opts?: MotorCasosLocalOptions) {
    this.config = opts?.config ?? MOTOR_CASOS_CONFIG_DEFAULT;
    this.eventStore = opts?.eventStore;
    this.onComponentesFaltantes = opts?.onComponentesFaltantes;
    this.casosState = opts?.casosIniciales ? [...opts.casosIniciales] : [];
  }

  /** Genera un `caso_id` único y razonablemente estable. */
  private nuevoCasoId(): Id {
    this.contador += 1;
    return generarId(`caso-${this.contador}`);
  }

  /** Snapshot de los Casos vigentes (lectura para vistas/tests). */
  casos(): Caso[] {
    return [...this.casosState];
  }

  /**
   * Correlaciona evidencia sobre su entidad raíz dentro de la ventana de
   * correlación: crea, actualiza o consolida el Caso correspondiente y
   * emite el Evento_Operacional del delta (R3.1, R3.2, R3.9, R9).
   */
  async ingest(evidencia: Evidencia): Promise<Result<CasoDelta>> {
    const aplicado = aplicarEvidencia(this.casosState, evidencia, {
      ventanaMs: this.config.ventanaCorrelacionMs,
      nuevoCasoId: () => this.nuevoCasoId(),
    });
    if (!aplicado.ok) return aplicado;

    this.casosState = aplicado.value.casos;
    await this.emitirEventoDelta(aplicado.value.delta, evidencia.ts);
    return { ok: true, value: aplicado.value.delta };
  }

  /** Emite un Evento_Operacional que registra el delta del Caso (R9). */
  private async emitirEventoDelta(delta: CasoDelta, ts: IsoTimestamp): Promise<void> {
    if (!this.eventStore) return;
    const evento: Omit<EventoOperacional, "event_id" | "seq" | "prev_hash" | "payload_hash"> = {
      tipo: `caso_${delta.operacion}`,
      entidad_ref: { tipo: "Caso", id: delta.caso.caso_id },
      actor: "motor_casos",
      ts: Number.isFinite(ms(ts)) ? ts : nowIso(),
      payload: {
        caso_id: delta.caso.caso_id,
        operacion: delta.operacion,
        entidad_raiz_ref: delta.caso.entidad_raiz_ref,
        evidencia_count: delta.caso.evidencia.length,
        ...(delta.consolidados ? { consolidados: delta.consolidados } : {}),
      },
      correlation_id: generarId("corr"),
    };
    // El append es append-only; un fallo no debe romper la correlación.
    await this.eventStore.append(evento);
  }

  // -------------------------------------------------------------------------
  // Stubs cubiertos por tareas posteriores (2.2 scoring, 2.7 explain/orden/…)
  // -------------------------------------------------------------------------

  /**
   * Calcula el Score_Caso como combinación lineal determinista de los
   * nueve componentes ponderados, acotada a `[0, 100]` (R3.3, R3.5, R3.6).
   *
   * Deriva los componentes del Caso con {@link componentesDeCaso} (pura,
   * determinista) y los combina con {@link scoreFromComponentes}. El
   * parámetro `code_version` se reserva para la paridad de fórmulas
   * demo↔real (R3.5): el resultado solo depende de `(caso, pesos)` y de
   * la versión de código, no del instante de ejecución.
   *
   * Si faltan datos de uno o más componentes, calcula con los disponibles,
   * marca el resultado como **parcial** y registra los faltantes en la
   * Observabilidad mediante el hook inyectado (R3.8).
   */
  score(caso: Caso, pesos: PesosScore, _code_version: string): Result<ScoreCaso> {
    const componentes = componentesDeCaso(caso);
    const resultado = scoreFromComponentes(componentes, pesos);
    if (resultado.parcial && this.onComponentesFaltantes) {
      this.onComponentesFaltantes(caso.caso_id, resultado.componentes_faltantes);
    }
    return { ok: true, value: resultado };
  }

  /**
   * Construye la {@link ExplainabilityCaso} del Caso (R4.1–R4.3): enumera
   * los nueve componentes en orden canónico con su contribución con signo
   * en `[-100, 100]`, marca como no disponibles los que carecen de datos
   * (contribución 0, sin omitirlos) y garantiza que la suma de las
   * contribuciones reconstruya el Score_Caso ±1.
   *
   * Deriva los componentes con {@link componentesDeCaso} y delega en
   * {@link explainFromComponentes} para mantener la **consistencia** con
   * {@link scoreFromComponentes} (mismos `peso_k · comp_k`).
   */
  explain(caso: Caso, pesos: PesosScore): Result<ExplainabilityCaso> {
    const componentes = componentesDeCaso(caso);
    return { ok: true, value: explainFromComponentes(componentes, pesos) };
  }

  /**
   * Orden total determinista de Casos (R3.4): descendente por
   * `score.valor`; empate → ascendente por `fecha_creacion`, luego
   * ascendente por `caso_id`. Delega en {@link ordenarCasos} (pura).
   */
  order(casos: Caso[]): Caso[] {
    return ordenarCasos(casos);
  }

  /**
   * Propone entre 0 y 10 acciones sugeridas vinculadas al
   * Registro_Acciones_Semanticas (R4.7, R4.8, R4.9). Cada acción adjunta
   * `razonamiento` y `evidencia`; sin acción aplicable, conjunto vacío.
   * Delega en {@link suggestActionsForCaso} (pura, determinista).
   */
  suggestActions(caso: Caso): Result<AccionSugerida[]> {
    return { ok: true, value: suggestActionsForCaso(caso) };
  }

  /**
   * Timeline de los {@link EventoOperacional} asociados al Caso ordenado
   * por `ts` en orden temporal no decreciente (R4.5). Si hay un
   * Event_Store inyectado, recopila sus eventos cuya `entidad_ref` es el
   * propio Caso (`tipo === "Caso"`, `id === caso_id`) iterando el stream
   * global; si no, sintetiza el timeline a partir de la evidencia del
   * Caso para no quedar vacío. El resultado se ordena con
   * {@link ordenarTimeline} (determinista).
   */
  async timeline(caso: Caso): Promise<Result<EventoOperacional[]>> {
    const eventos: EventoOperacional[] = [];

    if (this.eventStore) {
      try {
        for await (const ev of this.eventStore.streamFrom(0)) {
          if (
            ev.entidad_ref?.tipo === "Caso" &&
            ev.entidad_ref.id === caso.caso_id
          ) {
            eventos.push(ev);
          }
        }
      } catch (e) {
        // El timeline degrada de forma controlada ante fallos del stream.
        return {
          ok: false,
          error: `No se pudo leer el timeline del Caso '${caso.caso_id}': ${String(e)}`,
          degraded: true,
        };
      }
    }

    // Fallback / complemento: derivar eventos sintéticos desde la evidencia,
    // de modo que el timeline refleje las piezas correlacionadas (R4.5).
    if (eventos.length === 0) {
      for (const ev of caso.evidencia) {
        eventos.push(this.eventoDesdeEvidencia(caso, ev));
      }
    }

    return { ok: true, value: ordenarTimeline(eventos) };
  }

  /** Proyecta una pieza de evidencia del Caso a un Evento_Operacional sintético. */
  private eventoDesdeEvidencia(caso: Caso, ev: EvidenciaCaso): EventoOperacional {
    return {
      event_id: `${caso.caso_id}:${nodeIdFor(ev.evidencia_ref)}`,
      tipo: "evidencia_correlacionada",
      entidad_ref: { tipo: "Caso", id: caso.caso_id },
      actor: "motor_casos",
      ts: ev.ts,
      payload: { evidencia_ref: ev.evidencia_ref },
      seq: 0,
      prev_hash: null,
      payload_hash: "",
      correlation_id: `${caso.caso_id}:${nodeIdFor(ev.evidencia_ref)}`,
    };
  }
}
